// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./BulkAssignToolDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// API IMPORTS
import {
	getAPICall,
	postAPICall,
	deleteAPICall,
} from "../../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const BulkAssignToolDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [selectedTool, setSelectedTool] = useState(null);
	const [selectedStandardRoles, setSelectedStandardRoles] = useState([]);
	const [selectedAdminRoles, setSelectedAdminRoles] = useState([]);

	// Snapshot of who had the tool when it was selected, so submit can diff
	// against the live selection and add/remove as needed
	const [originalStandardRoleIds, setOriginalStandardRoleIds] = useState([]);
	const [originalAdminRoleIds, setOriginalAdminRoleIds] = useState([]);

	// Feedback states
	const [isLoading, setIsLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// Reset everything when the dialog opens
	useEffect(() => {
		if (props.open) {
			setSelectedTool(null);
			setSelectedStandardRoles([]);
			setSelectedAdminRoles([]);
			setOriginalStandardRoleIds([]);
			setOriginalAdminRoleIds([]);
			closeAlert();
		}
	}, [props.open]);

	// Look up who already has the tool whenever it changes, and pre-fill
	// both lists with the current assignment so it can be edited directly
	useEffect(() => {
		if (!selectedTool) {
			setSelectedStandardRoles([]);
			setSelectedAdminRoles([]);
			setOriginalStandardRoleIds([]);
			setOriginalAdminRoleIds([]);
			return;
		}

		let cancelled = false;

		const fetchAssignedRoles = async () => {
			try {
				const res = await getAPICall(
					instance,
					accounts[0],
					"/api/roles/tools/rolesByTool",
					{ toolID: selectedTool.id }
				);

				if (cancelled) return;

				const rows = res.data ?? [];
				const standardIds = rows
					.filter((r) => !r.admin_access)
					.map((r) => r.role_id);
				const adminIds = rows
					.filter((r) => r.admin_access)
					.map((r) => r.role_id);

				setOriginalStandardRoleIds(standardIds);
				setOriginalAdminRoleIds(adminIds);
				setSelectedStandardRoles(
					props.roles.filter((role) => standardIds.includes(role.id))
				);
				setSelectedAdminRoles(
					props.roles.filter((role) => adminIds.includes(role.id))
				);
			} catch (err) {
				console.error("Error fetching roles for tool:", err);
			}
		};

		fetchAssignedRoles();

		return () => {
			cancelled = true;
		};
	}, [selectedTool]);

	// INPUT HANDLERS
	const toolChanged = (event, newValue) => {
		setSelectedTool(newValue);
	};

	const standardRolesChanged = (event, newValue) => {
		setSelectedStandardRoles([...newValue]);
	};

	const adminRolesChanged = (event, newValue) => {
		setSelectedAdminRoles([...newValue]);
	};

	const selectAllStandard = () => setSelectedStandardRoles(standardOptions);
	const clearStandard = () => setSelectedStandardRoles([]);

	const selectAllAdmin = () => setSelectedAdminRoles(adminOptions);
	const clearAdmin = () => setSelectedAdminRoles([]);

	// Feedback handlers
	const openAlert = (message) => {
		setAlertOpen(true);
		setAlertMessage(message);
	};

	const closeAlert = () => {
		setAlertOpen(false);
		setAlertMessage("");
	};

	// Options available to pick from each list: not currently selected in the
	// other list (a role can't hold both Standard and Admin access at once)
	const standardOptions = props.roles.filter(
		(role) => !selectedAdminRoles.map((r) => r.id).includes(role.id)
	);

	const adminOptions = props.roles.filter(
		(role) => !selectedStandardRoles.map((r) => r.id).includes(role.id)
	);

	// SUBMIT
	const validateInputFields = () => {
		if (!selectedTool) {
			openAlert("Please select a tool.");
			return false;
		}

		if (
			hasCommonElements(
				selectedStandardRoles.map((r) => r.id),
				selectedAdminRoles.map((r) => r.id)
			)
		) {
			openAlert(
				"A role cannot be assigned as both Standard and Admin access in the same action."
			);
			return false;
		}

		return true;
	};

	const hasCommonElements = (arr1, arr2) => {
		return arr1.some((element) => arr2.includes(element));
	};

	const submitButtonClicked = async () => {
		if (!validateInputFields()) return;

		setIsLoading(true);

		try {
			const selectedStandardIds = selectedStandardRoles.map((r) => r.id);
			const selectedAdminIds = selectedAdminRoles.map((r) => r.id);

			const standardToAdd = selectedStandardIds.filter(
				(id) => !originalStandardRoleIds.includes(id)
			);
			const standardToRemove = originalStandardRoleIds.filter(
				(id) => !selectedStandardIds.includes(id)
			);
			const adminToAdd = selectedAdminIds.filter(
				(id) => !originalAdminRoleIds.includes(id)
			);
			const adminToRemove = originalAdminRoleIds.filter(
				(id) => !selectedAdminIds.includes(id)
			);

			// Removals go first so a role "moving" between Standard and Admin
			// doesn't have its freshly-added row immediately deleted again
			for (const roleID of [...standardToRemove, ...adminToRemove]) {
				await deleteAPICall(
					instance,
					accounts[0],
					"/api/roles/tools/deleteByRoleAndTool",
					{ roleID, toolID: selectedTool.id }
				);
			}

			for (const roleID of standardToAdd) {
				await postAPICall(instance, accounts[0], "/api/roles/tools/add", {
					roleID,
					toolID: selectedTool.id,
					modifiedBy: employeeID,
				});
			}

			for (const roleID of adminToAdd) {
				await postAPICall(
					instance,
					accounts[0],
					"/api/roles/tools/admin/add",
					{
						roleID,
						toolID: selectedTool.id,
						modifiedBy: employeeID,
					}
				);
			}

			props.fetchRoles();
			formClosed();
		} catch (err) {
			console.error("Error bulk assigning tool:", err);
			openAlert("An error occurred. Please try again.");
		}

		setIsLoading(false);
	};

	const formClosed = () => {
		props.bulkAssignToolDialogClosed();
	};

	// RENDER
	return (
		<Dialog
			id="bulkAssignToolDialog"
			open={props.open}
			fullWidth
			maxWidth="md"
			onClose={formClosed}
		>
			<DialogTitle id="bulkAssignToolDialogTitle">
				Assign Tool to Roles
			</DialogTitle>
			<DialogContent id="bulkAssignToolDialogContent">
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<>
						<div className="bulkAssignToolDialogRow">
							<Collapse sx={{ flex: 1 }} in={alertOpen}>
								<Alert
									severity="error"
									action={
										<IconButton
											aria-label="close"
											color="inherit"
											size="small"
											onClick={closeAlert}
										>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									}
								>
									{alertMessage}
								</Alert>
							</Collapse>
						</div>
						<div className="bulkAssignToolDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								id="bulkAssignToolSelect"
								value={selectedTool}
								onChange={toolChanged}
								options={props.tools}
								getOptionLabel={(option) => option.name}
								renderInput={(params) => (
									<TextField {...params} label="Tool" placeholder="Tool" />
								)}
							/>
						</div>
						<div className="bulkAssignToolDialogRow">
							<div className="bulkAssignToolDialogListColumn">
								<Autocomplete
									sx={{ flex: 1 }}
									limitTags={3}
									multiple
									disabled={!selectedTool}
									value={selectedStandardRoles}
									onChange={standardRolesChanged}
									options={standardOptions}
									getOptionLabel={(option) => option.role_name}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											label="Roles — Standard Access"
											placeholder="Roles"
										/>
									)}
								/>
								<div className="bulkAssignToolDialogListActions">
									<Button
										size="small"
										disabled={!selectedTool || standardOptions.length === 0}
										onClick={selectAllStandard}
									>
										Select All
									</Button>
									<Button
										size="small"
										disabled={selectedStandardRoles.length === 0}
										onClick={clearStandard}
									>
										Clear
									</Button>
								</div>
							</div>
							<div className="bulkAssignToolDialogListColumn">
								<Autocomplete
									sx={{ flex: 1 }}
									limitTags={3}
									multiple
									disabled={!selectedTool}
									value={selectedAdminRoles}
									onChange={adminRolesChanged}
									options={adminOptions}
									getOptionLabel={(option) => option.role_name}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											label="Roles — Admin Access"
											placeholder="Roles"
										/>
									)}
								/>
								<div className="bulkAssignToolDialogListActions">
									<Button
										size="small"
										disabled={!selectedTool || adminOptions.length === 0}
										onClick={selectAllAdmin}
									>
										Select All
									</Button>
									<Button
										size="small"
										disabled={selectedAdminRoles.length === 0}
										onClick={clearAdmin}
									>
										Clear
									</Button>
								</div>
							</div>
						</div>
						{selectedTool && (
							<div className="bulkAssignToolDialogRow bulkAssignToolDialogHint">
								Both lists are pre-filled with the roles that currently
								have this tool — add or remove roles as needed.
							</div>
						)}
					</>
				)}
				<div className="dialogButtons">
					<Button onClick={formClosed} variant="outlined" color="error">
						Cancel
					</Button>
					<Button onClick={submitButtonClicked} variant="contained">
						Submit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default BulkAssignToolDialog;
